import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from '../utils/api';
import '../styles/Settings.css';
import '../styles/DashboardHome.css';
import '../styles/Support.css';

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00`).toLocaleDateString();
};

const formatMoney = (amount, currency = 'USD') => {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const Subscription = () => {
  const [data, setData] = useState(null);
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Subscription';
    const load = async () => {
      setLoading(true);
      try {
        const [sub, notes] = await Promise.all([
          axios.get('/auth/subscription/'),
          axios.get('/auth/support/notifications/').catch(() => ({ data: { notifications: [] } })),
        ]);
        setData(sub.data);
        setNotices(notes.data?.notifications || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Subscription is not available yet.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`/auth/support/notifications/${id}/read/`, {});
      setNotices((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    } catch {
      // keep unread
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Subscription</h1>
        <p className="page-description">
          Your annual plan starts from the day this instance was registered with JEIC.
        </p>
      </div>

      {error && <Alert variant="warning" className="settings-alert">{error}</Alert>}

      {loading ? (
        <p>Loading subscription…</p>
      ) : data ? (
        <>
          <div className="stats-grid">
            {[
              { label: 'Status', value: (data.state || data.status || '—').replace('_', ' '), icon: 'fa-shield-alt' },
              { label: 'Renewal', value: formatDate(data.renewal_on), icon: 'fa-calendar-alt' },
              { label: 'Annual total', value: formatMoney(data.annual_total, data.currency), icon: 'fa-coins' },
              { label: 'Days left', value: data.days_until_renewal ?? '—', icon: 'fa-hourglass-half' },
            ].map((stat) => (
              <div key={stat.label} className="stat-card">
                <div className="stat-icon"><i className={`fas ${stat.icon}`}></i></div>
                <div className="stat-content">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-file-invoice-dollar"></i>
              <h2>{data.company_name || 'Plan details'}</h2>
            </div>
            <p>Started {formatDate(data.started_on)}. Last payment {formatDate(data.last_payment_on)}.</p>
            <p>Hosting {formatMoney(data.hosting_fee, data.currency)} · Service {formatMoney(data.service_charge, data.currency)}</p>
            <p>
              Need help or a payment confirmation?{' '}
              <Link to="/dashboard/support">Open a support ticket</Link>.
            </p>
            <h3 style={{ fontSize: 16, marginTop: 24 }}>Successful renewals</h3>
            {!(data.renewal_history || []).length ? (
              <p>No renewal history yet.</p>
            ) : (
              <ul className="notice-list">
                {(data.renewal_history || []).map((row) => (
                  <li key={row.id}>
                    <strong>{row.kind === 'registration' ? 'Registration' : 'Renewal'} · {formatDate(row.paid_on)}</strong>
                    <p>
                      Covered {formatDate(row.period_start)} – {formatDate(row.period_end)} · {formatMoney(row.annual_total, row.currency)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="settings-section">
            <div className="section-header">
              <i className="fas fa-bell"></i>
              <h2>Notices</h2>
            </div>
            {!notices.length ? (
              <p>No notices yet. You will be reminded 1 month, 2 weeks, 1 week, and 3 days before renewal.</p>
            ) : (
              <ul className="notice-list">
                {notices.map((note) => (
                  <li key={note.id} className={note.read_at ? 'notice-read' : ''}>
                    <strong>{note.title}</strong>
                    <p>{note.body}</p>
                    {!note.read_at && (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => markRead(note.id)}>
                        Mark read
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Subscription;
